// Shared filter bar for the Conversations workspace (List / Per-Stage /
// Per-Charger / Calendar). One controlled state object drives every view:
// Complete/Intake date-basis · free-text search · tech (charger) multi-select ·
// date range. Shop dark theme; mirrors the admin FilterBar's fields.
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Group,
  MultiSelect,
  Popover,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconCalendar, IconSearch } from '@tabler/icons-react';
import { shopApi } from '../api/client';

const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_MUTED = '#71717A';

export type DateBasis = 'complete' | 'intake';

export interface ConvFilters {
  q: string;
  from: Date | null;
  to: Date | null;
  dateBasis: DateBasis;
  techIds: number[];
}

export function defaultConvFilters(): ConvFilters {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29); // last 30 days, inclusive
  return { q: '', from, to, dateBasis: 'complete', techIds: [] };
}

export function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtRange(from: Date | null, to: Date | null): string {
  const f = (d: Date) => d.toLocaleString(undefined, { month: 'short', day: 'numeric' });
  if (from && to) return `${f(from)} — ${f(to)}`;
  if (from) return `From ${f(from)}`;
  if (to) return `Until ${f(to)}`;
  return 'Any date';
}

const PRESETS: { label: string; days: number }[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
];

const dateInputStyle: React.CSSProperties = {
  background: '#111113',
  border: `1px solid ${BORDER}`,
  borderRadius: 6,
  color: TEXT,
  fontSize: 12,
  padding: '6px 8px',
  colorScheme: 'dark',
  flex: 1,
};

export function ConversationFilterBar({
  value,
  onChange,
}: {
  value: ConvFilters;
  onChange: (f: ConvFilters) => void;
}) {
  const [members, setMembers] = useState<{ id: number; name: string }[]>([]);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    shopApi
      .listMembers()
      .then((m) => setMembers(m.map((x) => ({ id: x.id, name: x.name }))))
      .catch(() => {});
  }, []);

  const setPreset = (days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    onChange({ ...value, from, to });
    setDateOpen(false);
  };

  return (
    <Group gap={8} wrap="wrap" align="center">
      <SegmentedControl
        size="xs"
        value={value.dateBasis}
        onChange={(v) => onChange({ ...value, dateBasis: v as DateBasis })}
        data={[
          { label: 'Complete', value: 'complete' },
          { label: 'Intake', value: 'intake' },
        ]}
        styles={{
          root: { background: '#18181B', border: `1px solid ${BORDER}` },
          label: { color: TEXT_MUTED },
          indicator: { background: 'rgba(37,99,235,0.25)' },
        }}
      />
      <TextInput
        placeholder="Search customer, plate, VIN…"
        leftSection={<IconSearch size={14} color={TEXT_MUTED} />}
        value={value.q}
        onChange={(e) => onChange({ ...value, q: e.currentTarget.value })}
        size="xs"
        style={{ flex: 1, minWidth: 220 }}
        styles={{ input: { background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, '&:focus': { borderColor: '#3B82F6' } } }}
      />
      <MultiSelect
        placeholder="Any tech"
        data={members.map((m) => ({ value: String(m.id), label: m.name }))}
        value={value.techIds.map(String)}
        onChange={(vals) => onChange({ ...value, techIds: vals.map(Number) })}
        size="xs"
        searchable
        clearable
        style={{ minWidth: 160 }}
        styles={{ input: { background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT, fontSize: 13, '&:focusWithin': { borderColor: '#3B82F6' } } }}
        comboboxProps={{ withinPortal: true }}
      />
      <Popover opened={dateOpen} onChange={setDateOpen} position="bottom-end" withinPortal shadow="md" width={260}>
        <Popover.Target>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconCalendar size={14} />}
            onClick={() => setDateOpen((o) => !o)}
            styles={{ root: { background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT } }}
          >
            {fmtRange(value.from, value.to)}
          </Button>
        </Popover.Target>
        <Popover.Dropdown style={{ background: '#18181B', border: `1px solid ${BORDER}` }}>
          <Stack gap={6}>
            {PRESETS.map((p) => (
              <UnstyledButton
                key={p.days}
                onClick={() => setPreset(p.days)}
                style={{ padding: '6px 8px', borderRadius: 6, color: TEXT, fontSize: 13 }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = 'transparent')}
              >
                {p.label}
              </UnstyledButton>
            ))}
            <Text size="10px" c={TEXT_MUTED} tt="uppercase" mt={4} style={{ letterSpacing: 0.5 }}>
              Custom
            </Text>
            <Group gap={6} wrap="nowrap">
              <input
                type="date"
                value={toIsoDate(value.from) ?? ''}
                onChange={(e) =>
                  onChange({ ...value, from: e.currentTarget.value ? new Date(`${e.currentTarget.value}T00:00:00`) : null })
                }
                style={dateInputStyle}
              />
              <input
                type="date"
                value={toIsoDate(value.to) ?? ''}
                onChange={(e) =>
                  onChange({ ...value, to: e.currentTarget.value ? new Date(`${e.currentTarget.value}T00:00:00`) : null })
                }
                style={dateInputStyle}
              />
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}
